import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import DropdownList from "react-widgets/DropdownList";
import toast from "react-hot-toast";
import ItemService from "../../services/customer_appCommonService";
import ItemServiceV2 from "../../services/customer_v2_appCommonService";
import { getTodayDate, validateContactNumber, validateRequired, validPostalCode } from "../../common/Validations";
import CustomerConstants from "../../common/CustomerConstants";
import AccountsFlowConstants from "../../common/AccountsFlowConstants";
import { formatINR } from "../accounts/accountUser";
import PageShell from "../accounts/PageShell";
import CollapsibleSection from "../../Components/CollapsibleSection/CollapsibleSection";
import MaterialSelectionModal from "../customer_app/Customer_V2/Scheduling/MaterialSelectionModal";
import CreditPolicyRequestFormOrderBookingModal from "../customer_app/Customer_V2/CreditPolicy/CreditPolicyRequestFormOrderBookingModal";
import "../accounts/accounts.css";
import "./flow.css";

/*
 * Create Order as a full page: the same fields, rules and save (addOrderScheduleBooking) as the
 * Order Booking popup (Customer_V2/Scheduling/OrderSchedulingModal), laid out as cards.
 */

const EX_FOR = ["Ex-Work", "FOR"];
const OTHERS_BLANK = { customerName: "", address: "", address2: "", address3: "", state: "", cityPostalCode: "", certifications: "", otherCustomerContactName: "", otherCustomerContactNumber: "" };
const amountOf = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);
const isSet = (value) => value !== undefined && value !== null && value !== "";
const joinAddress = (parts) => parts.filter(Boolean).join(" ");
const companyAddressOf = (c) => joinAddress([c?.address, c?.address2, c?.address3, c?.city, c?.state]);
const customerAddressOf = (c) => joinAddress([c?.address, c?.address2, c?.address3, c?.state]);

/* Line amounts, as the popup works them out (unit price includes GST; reversePercentage takes it back out). */
function priced(row) {
  const qty = parseFloat(row.orderQuantityLoad) || 0;
  const unitPrice = parseFloat(row.unitPrice) || 0;
  const reverse = parseFloat(row.reversePercentage);
  const cgst = parseFloat(row.cgst);
  const sgst = parseFloat(row.sgst);
  const next = { ...row, gst: cgst + sgst };
  next.basicPrice = unitPrice / reverse;
  if (!Number.isNaN(next.basicPrice)) next.basicAmount = (next.basicPrice * qty).toFixed(2);
  next.rate = (qty * unitPrice).toFixed(2);
  if (!Number.isNaN(reverse) && reverse !== 0) next.basicPrice = (unitPrice / reverse).toFixed(2);
  if (!Number.isNaN(Number(next.basicAmount))) next.cgstValue = ((cgst * next.basicAmount) / 100).toFixed(2);
  if (!Number.isNaN(sgst) && !Number.isNaN(Number(next.basicAmount))) next.sgstValue = ((sgst * next.basicAmount) / 100).toFixed(2);
  next.gstValue = ((next.gst * next.basicAmount) / 100).toFixed(2);
  next.netValue = (parseFloat(next.gstValue) + parseFloat(next.basicAmount)).toFixed(2);
  return next;
}

function Field({ label, required, error, hint, span, children, box }) {
  // box: a div, not a <label>, for dropdown widgets (a label re-clicks them and they close at once).
  const Tag = box ? "div" : "label";
  return (
    <Tag className={`tally-field${span ? " accounts-span" : ""}`}>
      <span>{label}{required ? <span className="tally-error"> *</span> : null}</span>
      {children}
      {error ? <span className="accounts-hint tally-error">{error}</span> : hint ? <span className="accounts-hint">{hint}</span> : null}
    </Tag>
  );
}

export default function FlowOrderCreate() {
  const navigate = useNavigate();
  const userData = useSelector((state) => state?.user?.data);
  const [companies, setCompanies] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [company, setCompany] = useState(null);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [others, setOthers] = useState(false);
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState({});
  const [materialOpen, setMaterialOpen] = useState(false);
  const [creditRequestOpen, setCreditRequestOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState("");

  useEffect(() => {
    ItemService.getMasterData({ codeType: "companyCode" }).then((item) => setCompanies(item?.status === 200 ? item.data || [] : []));
  }, []);

  const pickCompany = (value) => {
    setCompany(value);
    setCustomer(null);
    setCustomerDetails(others ? { ...OTHERS_BLANK } : null);
    setCustomers([]);
    setErrors((e) => ({ ...e, companyCode: "" }));
    if (!value) return;
    ItemServiceV2.getCompanyMaster({ code: value.id }).then((item) => setCompanyDetails(item?.status === 200 ? item.data?.[0] || null : null));
    ItemService.getCustomersByCompany({ companyCode: value.id }).then((item) => setCustomers(item?.status === 200 ? item.data || [] : []));
  };

  const pickCustomer = (value) => {
    setCustomer(value);
    setCustomerDetails(null);
    setErrors((e) => ({ ...e, customerName: "" }));
    if (!value) return;
    ItemService.getCustomerDetail({ documentNumber: value.id }).then((item) => setCustomerDetails(item?.status === 200 ? item.data?.[0] || null : null));
  };

  const toggleOthers = (event) => {
    setOthers(event.target.checked);
    setCustomer(null);
    setCustomerDetails(event.target.checked ? { ...OTHERS_BLANK } : null);
    setErrors({});
  };

  const setOther = (name) => (event) => {
    const { value } = event.target;
    setCustomerDetails((d) => ({ ...(d || OTHERS_BLANK), [name]: value }));
    setErrors((e) => ({ ...e, [name]: "" }));
  };

  // Credit: creditLimitAmt (Credit Policy), else Customer Master creditLimit; none = no check.
  const creditLimitShown = isSet(customerDetails?.creditLimitAmt) ? customerDetails.creditLimitAmt : (isSet(customerDetails?.creditLimit) ? customerDetails.creditLimit : "");
  const creditPeriodShown = customerDetails?.creditPeriod || customerDetails?.creditDays || "";
  const currentAvailableAmt = !others && customerDetails && isSet(creditLimitShown)
    ? Math.round((amountOf(creditLimitShown) - amountOf(customerDetails.creditOutStandingAmt) - amountOf(customerDetails.pendingOrderValue)) * 100) / 100
    : null;
  const orderTotalValue = useMemo(() => rows.reduce((sum, row) => sum + (parseFloat(row.orderQuantityLoad) || 0) * (parseFloat(row.unitPrice) || 0), 0), [rows]);
  const overLimit = currentAvailableAmt !== null && orderTotalValue > currentAvailableAmt;

  /* Same checks as the popup's Add button, then the material picker. */
  const openMaterials = () => {
    const next = {};
    if (!validateRequired(company?.id)) next.companyCode = "Please fill the required field";
    else if (!others) {
      if (!validateRequired(customer?.name)) next.customerName = "Please fill the required field";
    } else {
      const d = customerDetails || {};
      if (!validateRequired(d.customerName)) next.customerName = "Please fill the required field";
      else if (!validateRequired(d.address)) next.address = "Please fill the required field";
      else if (!validateRequired(d.cityPostalCode)) next.cityPostalCode = "Please fill the required field";
      else if (!validPostalCode(d.cityPostalCode)) next.cityPostalCode = "Enter a 6-character postal code.";
      else if (!validateRequired(d.otherCustomerContactNumber)) next.otherCustomerContactNumber = "Please fill the required field";
      else if (!validateContactNumber(d.otherCustomerContactNumber)) next.otherCustomerContactNumber = "Please enter a valid contact number.";
    }
    setErrors(next);
    if (Object.keys(next).length === 0) setMaterialOpen(true);
  };

  const addMaterials = (selected) => {
    const shipping = customerAddressOf(customerDetails);
    setRows((current) => {
      const added = (selected || []).map((item) => ({
        ...item,
        dispatchDate: getTodayDate(""),
        orderStatus: "Pending",
        orderQuantityLoad: "",
        shippingAddress: shipping,
        shippingPostalCode: customerDetails?.cityPostalCode,
        customerAddress: shipping,
        customerPostalCode: customerDetails?.cityPostalCode,
        certifications: customerDetails?.certifications,
        companyAddress: companyAddressOf(companyDetails),
        companyPostalCode: companyDetails?.cityPostalCode,
        companyCertifications: companyDetails?.certifications,
      }));
      const unique = Array.from(new Map([...current, ...added].map((row) => [row._id, row])).values());
      return unique.map((row, index) => ({ ...row, local_id: index + 1 }));
    });
  };

  const setRow = (localId, name, number) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    if (number && Number.isNaN(Number(value))) {
      toast.error("Please enter a valid number", { duration: 3000, position: "top-right" });
      return;
    }
    setRows((current) => current.map((row) => (row.local_id === localId ? priced({ ...row, [name]: value }) : row)));
  };
  const setExFor = (localId) => (event) => {
    const { value } = event.target;
    setRows((current) => current.map((row) => (row.local_id === localId ? { ...row, exWork: value, exWorkCode: "", exWorkDescription: "" } : row)));
  };
  const removeRow = (localId) => setRows((current) => current.filter((row) => row.local_id !== localId).map((row, index) => ({ ...row, local_id: index + 1 })));

  const reset = () => {
    setCustomer(null);
    setCustomerDetails(others ? { ...OTHERS_BLANK } : null);
    setRows([]);
    setErrors({});
  };

  const save = async () => {
    setSaved("");
    if (rows.length === 0) return toast.error("Please add atleast one record!", { duration: 3000, position: "top-right" });
    if (rows.some((row) => !row.orderQuantityLoad || String(row.orderQuantityLoad).trim() === "")) return toast.error("Bag value should not be empty !!", { duration: 3000, position: "top-right" });
    if (rows.some((row) => !isSet(row.unitPrice) || String(row.unitPrice).trim() === "" || Number(row.unitPrice) === 0)) return toast.error("Unit price should not be empty or zero !!", { duration: 3000, position: "top-right" });

    // Same order record as the popup: header fields copied on every item.
    const header = {
      orderStatus: "Pending",
      orderPlacedBy: "",
      customerDetails: "",
      emailAddress: "",
      deliveryAddress: "",
      customerDetailsIfOther: "",
      createdDate: new Date(),
      createdBy: userData?.userId,
      dispatchDate: getTodayDate(""),
      companyCode: company?.id,
      companyCodeDesc: company?.description,
      ...(others ? {} : { customerName: customer?.name, customerCode: customer?.id, customerNameDesc: customer?.description, sapDocumentNumber: customer?.sapDocumentNumber, projectName: "" }),
    };
    const items = rows.map((row) => ({ ...row, ...header, dispatchDate: row.dispatchDate || header.dispatchDate })).map(({ _id, ...rest }) => rest);
    const d = customerDetails || {};
    const payload = {
      data: {
        companyCode: header.companyCode,
        companyCodeDesc: header.companyCodeDesc,
        customerCode: header.customerCode || "",
        customerName: header.customerName || d.customerName,
        deliveryName: header.projectName,
        address: d.address,
        address2: d.address2,
        address3: d.address3,
        state: d.state,
        cityPostalCode: d.cityPostalCode,
        certifications: d.certifications,
        creditLimitAmt: parseFloat(creditLimitShown) || "",
        creditOutStandingAmt: parseFloat(d.creditOutStandingAmt) || "",
        creditAvailableAmt: parseFloat(d.creditAvailableAmt) || "",
        creditLimitExpiryDate: d.creditLimitExpiryDate || "",
        creditPeriod: creditPeriodShown,
        orderTotalValue: parseFloat(orderTotalValue.toFixed(2)) || "",
        othersCustomerStatus: others,
        otherCustomerContactName: d.otherCustomerContactName || "",
        otherCustomerContactNumber: d.otherCustomerContactNumber || "",
        companyAddress: companyAddressOf(companyDetails),
        companyPostalCode: companyDetails?.cityPostalCode,
        companyCertifications: companyDetails?.certifications,
        companyBankDetails: companyDetails?.bandkDetails,
        companyContact: [{ contant1: "9448380669" }, { contant2: "7411677666" }],
        companyMail: "svah2003@gmail.com",
        salesOfficerCode: userData?.userId,
        salesOfficerName: userData?.firstName,
        salesOfficerEmail: userData?.email,
        salesOfficerContactNr: userData?.contactNumber,
        items,
        additionalCreditAmt: "",
      },
    };
    setSaving(true);
    try {
      const result = await ItemServiceV2.addOrderScheduleBookingV2(payload);
      if (result?.status === 200) {
        toast.success(result?.message || "added successfully", {
          duration: result?.creditHold ? 8000 : 2000,
          position: "top-right",
        });
        setSaved({ text: result?.message || "Order booked.", hold: Boolean(result?.creditHold), orderNo: result?.orderNo });
        reset();
      } else {
        toast.error(result?.message || "Please try again !!", { duration: 4000, position: "top-right" });
      }
    } finally {
      setSaving(false);
    }
  };

  const credit = !others && customerDetails;

  return (
    <PageShell
      title="Create Order"
      subtitle="Order booking · same fields and checks as the Order Booking popup"
      actions={<button type="button" className="btn btn-outline-primary" onClick={() => navigate(CustomerConstants.CUSTOMER_V2_ORDER_BOOKING_LIST_LINE_ITEM)}><i className="bi bi-list-check" /> Order Booking list</button>}
    >
      {saved && (
        <div className={saved.hold ? "accounts-note warn flow-order-note" : "accounts-success"}>
          <span>{saved.text}</span>
          {saved.orderNo && <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => navigate(`${AccountsFlowConstants.ORDER_TRAIL}?no=${saved.orderNo}`)}><i className="bi bi-diagram-3" /> Order details</button>}
          <button type="button" className="btn btn-sm btn-outline-success" onClick={() => navigate(CustomerConstants.CUSTOMER_V2_ORDER_BOOKING_LIST_LINE_ITEM)}><i className="bi bi-eye" /> Open list</button>
        </div>
      )}
      <div className="flow-order">
        <CollapsibleSection title="Customer details" subtitle="Company and customer">
          <div className="accounts-panel">
            <div className="accounts-entry-grid">
              <Field box label="Company Name" required error={errors.companyCode}>
                <DropdownList data={companies} dataKey="id" textField="name" filter="contains" value={company} onChange={pickCompany} placeholder="Select company" className={errors.companyCode ? "border-red-dropdown" : ""} />
              </Field>
              <Field label="Company Address">
                <textarea className="form-control" rows={2} readOnly value={companyAddressOf(companyDetails)} />
              </Field>
              {!others && (
                <>
                  <Field box label="Customer Name" required error={errors.customerName} hint={company ? `${customers.length} customers for this company` : "Pick the company first"}>
                    <DropdownList data={customers} dataKey="id" textField="name" filter="contains" value={customer} onChange={pickCustomer} placeholder="Select customer" disabled={!company} className={errors.customerName ? "border-red-dropdown" : ""} />
                  </Field>
                  <Field label="Customer Address">
                    <textarea className="form-control" rows={2} readOnly value={joinAddress([customerDetails?.address, customerDetails?.address2, customerDetails?.address3, customerDetails?.state, customerDetails?.cityPostalCode])} />
                  </Field>
                  <Field label="Customer Code">
                    <input className="form-control" readOnly value={customerDetails?.customerCode || ""} />
                  </Field>
                </>
              )}
              <label className="accounts-check flow-order-others">
                <input type="checkbox" className="form-check-input" checked={others} onChange={toggleOthers} />
                <span>Others (customer not in Customer Master)</span>
              </label>
              {others && (
                <>
                  <Field label="Customer Name" required error={errors.customerName}>
                    <input className={`form-control${errors.customerName ? " is-invalid" : ""}`} value={customerDetails?.customerName || ""} onChange={setOther("customerName")} />
                  </Field>
                  <Field label="Address" required error={errors.address}>
                    <textarea className={`form-control${errors.address ? " is-invalid" : ""}`} rows={2} value={customerDetails?.address || ""} onChange={setOther("address")} />
                  </Field>
                  <Field label="Pin Code" required error={errors.cityPostalCode}>
                    <input className={`form-control${errors.cityPostalCode ? " is-invalid" : ""}`} inputMode="numeric" maxLength={6} value={customerDetails?.cityPostalCode || ""} onChange={setOther("cityPostalCode")} />
                  </Field>
                  <Field label="Contact Name">
                    <input className="form-control" value={customerDetails?.otherCustomerContactName || ""} onChange={setOther("otherCustomerContactName")} />
                  </Field>
                  <Field label="Contact Number" required error={errors.otherCustomerContactNumber}>
                    <input className={`form-control${errors.otherCustomerContactNumber ? " is-invalid" : ""}`} inputMode="tel" maxLength={10} value={customerDetails?.otherCustomerContactNumber || ""} onChange={setOther("otherCustomerContactNumber")} />
                  </Field>
                </>
              )}
            </div>
          </div>
        </CollapsibleSection>

        {credit && (
          <CollapsibleSection title="Customer credit limit" subtitle="Credit limit details">
            <div className="accounts-panel">
              <div className="accounts-facts">
                <div><span className="accounts-hint">Credit Limit Amt</span><strong>{isSet(creditLimitShown) ? formatINR(creditLimitShown) : "No credit limit set"}</strong></div>
                <div><span className="accounts-hint">Current Out Standing Amt</span><strong>{formatINR(amountOf(customerDetails.creditOutStandingAmt))}</strong></div>
                <div><span className="accounts-hint">Credit Period (Days)</span><strong>{creditPeriodShown || "—"}</strong></div>
                <div><span className="accounts-hint">Order Value</span><strong className={overLimit ? "tally-error" : ""}>{formatINR(orderTotalValue)}</strong></div>
                <div><span className="accounts-hint">Pending Order Amt</span><strong>{formatINR(amountOf(customerDetails.pendingOrderValue))}</strong></div>
                <div><span className="accounts-hint">Current Available Amt</span><strong className={currentAvailableAmt !== null && currentAvailableAmt < 0 ? "tally-error" : ""}>{currentAvailableAmt === null ? "No credit limit set" : formatINR(currentAvailableAmt)}</strong></div>
                <div><span className="accounts-hint">Limit to be Extended (Amount)</span><strong>{overLimit ? formatINR(Math.round((orderTotalValue - currentAvailableAmt) * 100) / 100) : "—"}</strong></div>
                <div><span className="accounts-hint">Period to be Extended (Days)</span><strong>—</strong></div>
              </div>
              {overLimit ? (
                <div className="accounts-note warn flow-order-note">
                  <span>The order value is more than the available credit. You can still save it: it goes on <strong>credit hold</strong>, and Loading Advice can be created once an approver approves it. Or ask for a credit limit extension.</span>
                  <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setCreditRequestOpen(true)}>Credit Request</button>
                </div>
              ) : currentAvailableAmt !== null ? (
                <div className="accounts-note ok">Orders up to {formatINR(currentAvailableAmt - orderTotalValue)} more can be added (available − order value).</div>
              ) : (
                <div className="accounts-note">No credit limit is set for this customer, so the order is not checked against one.</div>
              )}
            </div>
          </CollapsibleSection>
        )}

        <CollapsibleSection
          title="Product details"
          subtitle={rows.length ? `${rows.length} item${rows.length > 1 ? "s" : ""}` : "Add the materials to order"}
          actions={[{ id: "add", label: "Add materials", icon: "bi-plus-lg", onClick: openMaterials }]}
        >
          <div className="accounts-panel">
            {rows.length === 0 && (
              <div className="flow-order-empty">
                <p className="accounts-hint">No materials yet. Pick the company and customer, then add materials.</p>
                <button type="button" className="btn btn-outline-primary" onClick={openMaterials}><i className="bi bi-plus-lg" /> Add materials</button>
              </div>
            )}
            {rows.map((row) => (
              <div className="flow-order-line" key={row.local_id}>
                <div className="flow-order-line-head">
                  <div>
                    <strong>{row.local_id}. {row.materialLongText}</strong>
                    <span className="accounts-hint">{row.materialCode}{row.uOM ? ` · ${row.uOM}` : ""}</span>
                  </div>
                  <div className="flow-order-line-amount">
                    <strong>{formatINR((parseFloat(row.orderQuantityLoad) || 0) * (parseFloat(row.unitPrice) || 0))}</strong>
                    <button type="button" className="accounts-icon-btn" title="Remove" aria-label={`Remove ${row.materialLongText}`} onClick={() => removeRow(row.local_id)}><i className="bi bi-trash text-danger" /></button>
                  </div>
                </div>
                <div className="flow-order-line-grid">
                  <Field label={`Qty${row.uOM ? ` (${row.uOM})` : ""}`} required>
                    <input className="form-control" inputMode="decimal" value={row.orderQuantityLoad || ""} onChange={setRow(row.local_id, "orderQuantityLoad", true)} />
                  </Field>
                  <Field label="Unit Price (₹)" required>
                    <input className="form-control" inputMode="decimal" value={row.unitPrice || ""} onChange={setRow(row.local_id, "unitPrice", true)} />
                  </Field>
                  <Field label="EX/FOR">
                    <select className="form-select" value={row.exWork || ""} onChange={setExFor(row.local_id)}>
                      <option value="">Select</option>
                      {EX_FOR.map((option) => <option key={option} value={option}>{option}</option>)}
                    </select>
                  </Field>
                  <Field label="Dispatch Date">
                    <input className="form-control" type="date" value={row.dispatchDate || getTodayDate("")} onChange={setRow(row.local_id, "dispatchDate")} />
                  </Field>
                  <label className="accounts-check flow-order-ac">
                    <input type="checkbox" className="form-check-input" role="switch" checked={row.accOnOff === true} onChange={setRow(row.local_id, "accOnOff")} />
                    <span>A/C</span>
                  </label>
                  <Field label="Shipping Postal Code">
                    <input className="form-control" inputMode="numeric" value={row.shippingPostalCode || ""} onChange={setRow(row.local_id, "shippingPostalCode", true)} />
                  </Field>
                  <Field label="Shipping Address" span>
                    <textarea className="form-control" rows={2} value={row.shippingAddress || ""} onChange={setRow(row.local_id, "shippingAddress")} />
                  </Field>
                  <Field label="Remarks" span>
                    <textarea className="form-control" rows={2} value={row.dispatchRemarks || ""} onChange={setRow(row.local_id, "dispatchRemarks")} />
                  </Field>
                </div>
              </div>
            ))}
            {rows.length > 0 && (
              <div className="flow-order-total">
                <span>Order value</span>
                <strong className={overLimit ? "tally-error" : ""}>{formatINR(orderTotalValue)}</strong>
              </div>
            )}
          </div>
        </CollapsibleSection>
      </div>
      <div className="accounts-form-actions">
        <button className="btn btn-outline-secondary" type="button" onClick={reset} disabled={saving}>Clear</button>
        <button className="btn tally-btn" type="button" onClick={save} disabled={saving}>{saving ? "Saving…" : overLimit ? "Save on credit hold" : "Save order"}</button>
      </div>
      <MaterialSelectionModal show={materialOpen} onHide={() => setMaterialOpen(false)} onselectedcatdata={addMaterials} />
      <CreditPolicyRequestFormOrderBookingModal
        show={creditRequestOpen}
        onHide={() => setCreditRequestOpen(false)}
        getCustomerDetails={customerDetails}
        getCompanyDetails={companyDetails}
        currentAvailableAmt={currentAvailableAmt}
      />
    </PageShell>
  );
}
